import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { isAuthenticated } from '../../../../services/auth';
import moment from 'moment';
import throttle from 'lodash/throttle';
import { message, Popconfirm, Pagination } from 'antd';
import { addLocation, allLocations, deleteLocation } from '../../../../services/admin';
import UnifiedDashboard from '../../../../components/layout/UnifiedDashboard';
import './AddLocation.css';

const AddLocation = () => {
  const [name, setName] = useState('');
  const countPerPage = 5;
  const [locations, setLocations] = useState({
    location: [],
    pagination: [],
    deleted: false,
    newAdded: false,
  });
  const [current, setCurrent] = useState();
  const [search, setSearch] = useState('');
  const { location, pagination, deleted, newAdded } = locations;

  const { token } = isAuthenticated();

  const searchData = useRef(
    throttle(async (val) => {
      const query = val.toLowerCase();
      setCurrent(1);
      const data = await allLocations();
      const newData = data.data
        .filter((item) => item.name.toLowerCase().indexOf(query) > -1)
        .slice(0, countPerPage);
      setLocations({ ...locations, pagination: newData });
    }, 400)
  );

  useEffect(() => {
    if (!search) {
      loadLocations(1);
    } else {
      searchData.current(search);
    }
  }, [search, newAdded, deleted]);

  const loadLocations = async (page) => {
    const res = await allLocations();
    setCurrent(page);
    const to = page * countPerPage;
    const from = to - countPerPage;
    setLocations({ location: res.data, pagination: res.data.slice(from, to) });
  };

  const handleDelete = async (locationId) => {
    const res = await deleteLocation(locationId, token);
    if (res.error) {
      console.log(res.error);
    } else {
      console.log(res);
      message.success('Localização excluída', 4);
      setLocations({ ...locations, deleted: true });
    }
  };

  const handleChange = (e) => {
    setName(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await addLocation({ name });
      console.log(res);
      message.success('Localização adicionada', 4);
      setName('');
      setLocations({ ...locations, newAdded: true });
    } catch (err) {
      console.log(err);
      if (err.response.status === 400) message.error(err.response.data, 4);
    }
  };

  return (
    <UnifiedDashboard title="Adicionar Localização">
      <div className='add-location-container'>
        {/* Header Section */}
        <div className='add-location-header'>
          <div className='add-location-header-content'>
            <h2 className='add-location-title'>
              <i className='fas fa-map-marker-alt'></i>
              Adicionar Localização
            </h2>
            <p className='add-location-subtitle'>Crie uma nova localização para o sistema</p>
          </div>
        </div>

        {/* Form Card */}
        <div className="add-location-form-card">
          <div className="add-location-form-wrapper">
            <form onSubmit={handleSubmit}>
              <div className="add-location-form-group">
                <label className="add-location-label">
                  <i className="fas fa-map-pin me-2"></i>
                  Nome da Localização
                </label>
                <input
                  type='text'
                  className='add-location-input'
                  placeholder='Digite o nome da localização (ex: São Paulo)'
                  value={name}
                  onChange={handleChange}
                  required
                />
              </div>
              <button
                type="submit"
                className="add-location-submit-btn"
              >
                <i className="fas fa-plus me-2"></i>
                Adicionar Localização
              </button>
            </form>
          </div>
        </div>

        {/* List Card */}
        <div className="add-location-list-card">
          <div className="add-location-list-header">
            <h3 className="add-location-list-title">
              <i className="fas fa-list"></i>
              Localizações Registradas
            </h3>
            <div className="add-location-search-wrapper">
              <i className="fas fa-search add-location-search-icon"></i>
              <input
                type='text'
                className='add-location-search-input'
                placeholder='Buscar localizações...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="add-location-list-body">
            {pagination && pagination.length > 0 ? (
              <>
                <div className="add-location-table-wrapper">
                  <table className='add-location-table'>
                    <thead>
                      <tr>
                        <th>
                          <i className="fas fa-map-pin me-2"></i>
                          Nome
                        </th>
                        <th>
                          <i className="fas fa-calendar me-2"></i>
                          Data de Adição
                        </th>
                        <th className="text-center">
                          <i className="fas fa-cogs me-2"></i>
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagination.map((c, i) => (
                        <tr key={i}>
                          <td>
                            <Link
                              to={`/location/${c._id}`}
                              className='add-location-table-link'
                            >
                              <i className="fas fa-folder me-2"></i>
                              {c.name}
                            </Link>
                          </td>
                          <td className="add-location-table-date">
                            <i className="fas fa-clock me-2"></i>
                            {moment(c.createdAt).format('DD/MM/YYYY')}
                          </td>
                          <td className="add-location-table-actions">
                            <Link
                              to={`/admin/location/edit/${c._id}`}
                              className='add-location-btn add-location-btn-edit'
                            >
                              <i className="fas fa-edit me-1"></i>
                              Editar
                            </Link>
                            <Popconfirm
                              placement='top'
                              title='Tem certeza que deseja excluir esta localização?'
                              onConfirm={() => handleDelete(c._id)}
                              okText='Sim'
                              cancelText='Não'
                            >
                              <button className='add-location-btn add-location-btn-delete'>
                                <i className="fas fa-trash me-1"></i>
                                Excluir
                              </button>
                            </Popconfirm>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="add-location-pagination">
                  <Pagination
                    pageSize={countPerPage}
                    onChange={loadLocations}
                    defaultCurrent={current}
                    total={location.length}
                    showSizeChanger={false}
                    showQuickJumper
                    showTotal={(total, range) => 
                      `${range[0]}-${range[1]} de ${total} localizações`
                    }
                  />
                </div>
              </>
            ) : (
              <div className="add-location-empty-state">
                <i className="fas fa-inbox"></i>
                <h5>Nenhuma localização encontrada</h5>
                <p>
                  {search ? 'Tente buscar com outros termos' : 'Adicione sua primeira localização acima'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </UnifiedDashboard>
  );
};

export default AddLocation;